import { Button, Chip } from '@nextui-org/react'
import { FC, useCallback, useMemo, useState } from 'react'
import { MdFileDownload } from 'react-icons/md'
import ApiStateView from '../ApiState'
import BackButton from '../BackButton'
import LogiSearch from '../LogiSearch'
import LogiTable from '../LogiTable'
import Panel from '../Panel'
import SectionTitle from '../SectionTitle'
import { useApi } from '../../hooks/useApi'
import { useSelectedSeller } from '../../hooks/useSelectedSeller'
import { useLogisticBox } from '../../context/LogisticBoxContext'
import { suppliesApi, SupplyShipmentItem } from '../../api'
import { ToneKey } from '../../models'

const SHIP_TONE: Record<string, ToneKey> = { lista: 'blue', en_transito: 'amber', entregada: 'green', atrasada: 'rosa' }
const SHIP_LABEL: Record<string, string> = { lista: 'Despachado', en_transito: 'En tránsito', entregada: 'Entregado', atrasada: 'Atrasado' }

// Tono interno → color de Chip de NextUI.
const chipColor = (tone: ToneKey): 'success' | 'warning' | 'danger' | 'primary' | 'secondary' | 'default' => {
  if (tone === 'green') return 'success'
  if (tone === 'amber') return 'warning'
  if (tone === 'rosa') return 'danger'
  if (tone === 'blue') return 'primary'
  if (tone === 'lila') return 'secondary'
  return 'default'
}

const fmtFecha = (iso: string): string => {
  const d = new Date(iso)
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')} · ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

const columns = [
  { name: 'Tienda destino', uid: 'store' },
  { name: 'Contenido', uid: 'contenido' },
  { name: 'Tracking #', uid: 'tracking' },
  { name: 'Estado', uid: 'estado' },
  { name: 'ETA', uid: 'eta' },
  { name: '', uid: 'actions' }
]

const TodosEnviosView: FC = () => {
  const idSeller = useSelectedSeller()
  const { goToShipmentTracking } = useLogisticBox()
  const [search, setSearch] = useState('')

  const data = useApi(useCallback(async () => await suppliesApi.shipments(idSeller), [idSeller]), [idSeller])

  const rows = useMemo(
    () => (data.data?.items ?? []).filter(e =>
      `${e.store} ${e.tracking_number ?? ''}`.toLowerCase().includes(search.toLowerCase())
    ),
    [data.data, search]
  )

  const renderCell = (e: SupplyShipmentItem, columnKey: string): JSX.Element => {
    switch (columnKey) {
      case 'store':
        return <span className='font-medium text-xs'>{e.store}</span>
      case 'contenido':
        return <span className='text-xs'>{e.contenido_resumen ?? '—'}</span>
      case 'tracking':
        return <span className=' text-xs'>{e.tracking_number ?? '—'}</span>
      case 'estado':
        return (
          <Chip size='sm' variant='flat' color={chipColor(SHIP_TONE[e.estado_local] ?? 'blue')}>
            {SHIP_LABEL[e.estado_local] ?? e.estado_local}
          </Chip>
        )
      case 'eta':
        return <span className='text-xs'>{e.eta_entrega !== null ? fmtFecha(e.eta_entrega) : '—'}</span>
      case 'actions':
        return (
          <div className='flex gap-2 justify-end items-center whitespace-nowrap'>
            <Button
              size='sm' className='bg-black text-white text-xs h-8'
              isDisabled={e.tracking_number === null}
              onPress={() => { if (e.tracking_number !== null) goToShipmentTracking(e.tracking_number) }}
            >
              Ver tracking
            </Button>
          </div>
        )
      default:
        return <span className='text-xs'>{(e as any)[columnKey]}</span>
    }
  }

  return (
    <div className='space-y-4'>
      <BackButton to='insumos' />

      <Panel>
        <div className='flex items-center justify-between mb-4 flex-wrap gap-3'>
          <SectionTitle title={`Envíos de insumos en curso · ${data.data?.items.length ?? 0}`} description='Tracking de paquetes despachados desde bodega central' />
          <div className='flex gap-2 flex-wrap items-center'>
            <LogiSearch className='w-56' placeholder='Buscar tracking # o tienda…' value={search} onChange={setSearch} />
            <Button variant='bordered' className='border-gray-200 text-xs' startContent={<MdFileDownload className='text-base' />}>Exportar</Button>
          </div>
        </div>

        <ApiStateView
          loading={data.loading} error={data.error} onRetry={data.reload}
          empty={!data.loading && rows.length === 0} emptyText='Sin envíos activos'
        >
          <LogiTable
            ariaLabel='Envíos de insumos en curso'
            columns={columns}
            items={rows}
            renderCell={renderCell}
            rowKey={(e) => e.id_shipment}
            loading={data.loading}
            emptyText='Sin envíos activos'
            rowsPerPage={10}
            bottomExtra={
              <div className='flex items-center justify-between text-xs text-[#151930]/60'>
                <span>Mostrando {rows.length} de {data.data?.items.length ?? 0} envíos</span>
              </div>
            }
          />
        </ApiStateView>
      </Panel>
    </div>
  )
}

export default TodosEnviosView
